import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { useTenant } from "@/contexts/TenantContext";
import { buildTenantPath } from "@/lib/tenantPath";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Plus, ArrowRight, Trash2, Layers, Building } from "lucide-react";
import { usePageTitle } from "@/contexts/PageTitleContext";

export default function PlaybooksPage() {
  usePageTitle("Playbooks");
  const navigate = useNavigate();
  const user = getStoredUser();
  const isSuper = user?.role === "super_admin";
  const isAdmin = user?.role === "admin";
  const isAdminUser = isSuper || isAdmin;
  
  // P0 TENANT ISOLATION: Use global tenant context (no account selector!)
  const { selectedTenantId } = useTenant();
  
  // P0 TENANT ISOLATION: Use centralized tenant path builder
  const buildPath = useCallback((basePath) => {
    return buildTenantPath(basePath, selectedTenantId);
  }, [selectedTenantId]);

  // Workspace RBAC state
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(() => {
    // Default: "all" for admins, first workspace for non-admins (set after fetch)
    return isAdminUser ? "all" : null;
  });
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newWorkspaceId, setNewWorkspaceId] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePt, setDeletePt] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Fetch workspaces for the workspace selector
  const fetchWorkspaces = async () => {
    setWorkspacesLoading(true);
    try {
      const res = await api.get("/tenants/workspaces");
      const ws = res.data || [];
      setWorkspaces(ws);
      
      // For non-admins, default to first assigned workspace
      if (!isAdminUser && ws.length > 0 && !selectedWorkspaceId) {
        setSelectedWorkspaceId(ws[0].id);
      }
    } catch (e) {
      toast({
        title: "Could not load workspaces",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setWorkspacesLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    setLoading(true);
    try {
      const params = {};
      // Only add workspace_id if not "all"
      if (selectedWorkspaceId && selectedWorkspaceId !== "all") {
        params.workspace_id = selectedWorkspaceId;
      }
      const res = await api.get("/product-types", { params });
      setProductTypes(res.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast({
          title: "Access denied",
          description: "You do not have access to this workspace.",
          variant: "destructive",
        });
        setProductTypes([]);
      } else {
        toast({
          title: "Could not load playbooks",
          description: e?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch workspaces on mount and when tenant changes
  // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only fetch when selectedTenantId exists
  useEffect(() => {
    if (!selectedTenantId) return; // Prevent premature API calls
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId]);

  // Refresh workspaces when user returns to page (e.g., after creating workspace in Admin)
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === 'visible' && selectedTenantId) {
        fetchWorkspaces();
      }
    };
    
    document.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedTenantId]);

  // Fetch playbooks when workspace selection or tenant changes
  // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only fetch when selectedTenantId exists
  useEffect(() => {
    if (!selectedTenantId) return; // Prevent premature API calls
    // Wait for workspace selection to be set for non-admins
    if (!isAdminUser && !selectedWorkspaceId && !workspacesLoading) {
      return;
    }
    fetchProductTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, selectedTenantId, workspacesLoading]);

  const onCreate = async () => {
    try {
      const payload = {
        name: newName,
        workspace_id: newWorkspaceId,  // Required field
      };
      const res = await api.post("/product-types", payload);
      toast({ title: "Playbook created" });
      setCreateOpen(false);
      setNewName("");
      setNewWorkspaceId("");
      navigate(buildPath(`/app/playbooks/${res.data.id}`));
    } catch (e) {
      toast({
        title: "Create failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const canDelete = isAdminUser;
  const canCreate = isAdminUser;

  const onDelete = async () => {
    if (!deletePt) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/product-types/${deletePt.id}`);
      toast({ title: "Playbook deleted" });
      setDeleteOpen(false);
      setDeletePt(null);
      await fetchProductTypes();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteBusy(false);
    }
  };

  // Get workspace name for display
  const getWorkspaceName = (workspaceId) => {
    const ws = workspaces.find(w => w.id === workspaceId);
    return ws?.name || "Unassigned";
  };

  return (
    <div className="space-y-6" data-testid="playbooks-page">
      {/* P0 WORKSPACE RBAC: Workspace slider (Tabs) - matches Admin page styling */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="playbooks-controls">
        <div className="flex flex-wrap items-center gap-3" data-testid="playbooks-workspace-picker">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-200/70">Workspace</span>
          </div>
          
          {workspacesLoading ? (
            <span className="text-sm text-slate-400">Loading...</span>
          ) : workspaces.length === 0 && !isAdminUser ? (
            <span className="text-sm text-amber-400">
              No workspaces assigned. Contact your admin.
            </span>
          ) : (
            <Tabs 
              value={selectedWorkspaceId || ""} 
              onValueChange={setSelectedWorkspaceId}
              className="w-fit"
              data-testid="playbooks-workspace-tabs"
            >
              <TabsList
                className="inline-flex h-auto w-fit max-w-full flex-wrap justify-start gap-1 bg-white/5 border-white/10"
                data-testid="playbooks-workspace-tabs-list"
              >
                {/* "All Workspaces" only for admins */}
                {isAdminUser && (
                  <TabsTrigger 
                    value="all"
                    className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300"
                    data-testid="workspace-tab-all"
                  >
                    <Building className="h-4 w-4 mr-1.5" />
                    All
                  </TabsTrigger>
                )}
                {workspaces.map((ws) => (
                  <TabsTrigger 
                    key={ws.id} 
                    value={ws.id}
                    className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300"
                    data-testid={`workspace-tab-${ws.id}`}
                  >
                    <Building className="h-4 w-4 mr-1.5" />
                    {ws.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </div>

        {canCreate && (
          <div className="flex flex-wrap items-center gap-2" data-testid="playbooks-actions">
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-playbook-open-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Playbook
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border-white/10" data-testid="create-product-type-dialog">
                <DialogHeader>
                  <DialogTitle className="text-slate-100" data-testid="create-playbook-title">
                    Create playbook
                  </DialogTitle>
                  <DialogDescription className="text-slate-200/70" data-testid="create-product-type-description">
                    This creates a dedicated scripts page and default script types.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-200/80" data-testid="create-product-type-name-label">
                      Name <span className="text-rose-400">*</span>
                    </label>
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Mortgage Protection"
                      className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                      data-testid="create-product-type-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-200/80">
                      Workspace <span className="text-rose-400">*</span>
                    </label>
                    {workspaces.filter(ws => ws.name?.toLowerCase() !== "unassigned").length === 0 ? (
                      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
                        No workspaces available. Please create a workspace in Admin → Workspaces before creating a playbook.
                      </div>
                    ) : (
                      <>
                        <Select value={newWorkspaceId} onValueChange={setNewWorkspaceId}>
                          <SelectTrigger className="bg-white/5 text-slate-100 ring-1 ring-white/10">
                            <SelectValue placeholder="Select workspace" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10">
                            {workspaces
                              .filter(ws => ws.name?.toLowerCase() !== "unassigned")
                              .map((ws) => (
                                <SelectItem key={ws.id} value={ws.id}>
                                  {ws.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {!newWorkspaceId && (
                          <div className="text-xs text-rose-400">
                            Please select a workspace
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={onCreate}
                    disabled={!newName.trim() || !newWorkspaceId}
                    data-testid="create-product-type-submit-button"
                  >
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {loading ? (
        <div className="muted" data-testid="playbooks-loading-text">
          Loading…
        </div>
      ) : productTypes.length === 0 ? (
        <div className="muted" data-testid="playbooks-empty-text">
          {isAdminUser
            ? selectedWorkspaceId === "all"
              ? "No playbooks found. Create one to start adding scripts."
              : "No playbooks in this workspace. Create one to start adding scripts."
            : "No playbooks assigned to your workspaces. Ask an Admin to grant access."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="playbooks-grid">
          {productTypes.map((pt) => (
            <Card key={pt.id} className="glass" data-testid={`product-type-card-${pt.id}`}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="min-w-0">
                  <CardTitle className="truncate text-slate-100" data-testid={`product-type-title-${pt.id}`}>
                    {pt.name}
                  </CardTitle>
                  {pt.workspace_id && (
                    <Badge 
                      variant="secondary" 
                      className="mt-2 bg-white/10 text-slate-300"
                      data-testid={`product-type-workspace-${pt.id}`}
                    >
                      {getWorkspaceName(pt.workspace_id)}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canDelete && (
                    <Dialog
                      open={deleteOpen && deletePt?.id === pt.id}
                      onOpenChange={(open) => {
                        setDeleteOpen(open);
                        setDeletePt(open ? pt : null);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="icon"
                          data-testid={`product-type-delete-open-${pt.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="glass border-white/10" data-testid={`product-type-delete-dialog-${pt.id}`}>
                        <DialogHeader>
                          <DialogTitle className="text-slate-100">Delete product type</DialogTitle>
                          <DialogDescription className="text-slate-200/70">
                            This will delete the product type and all scripts under it (script types, scripts, nodes, and options). Analytics data will remain.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="text-sm text-slate-200/80">
                          Delete <span className="font-semibold">{pt.name}</span>?
                        </div>
                        <DialogFooter>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setDeleteOpen(false);
                              setDeletePt(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={onDelete}
                            disabled={deleteBusy}
                            data-testid={`product-type-delete-confirm-${pt.id}`}
                          >
                            {deleteBusy ? "Deleting…" : "Delete"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}

                  <Button
                    asChild
                    variant="outline"
                    data-testid={`product-type-open-button-${pt.id}`}
                  >
                    <Link to={`/app/playbooks/${pt.id}`}>
                      Open
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
